import './AddressAutoCompleteInput.scss';

import { mdiMagnify } from '@mdi/js';
import type { InputRef } from 'antd';
import { Select } from 'antd';
import type { AddressSearchProvider } from 'api/models/mainservice';
import Icon from 'components/Icon/Icon';
import ImageComponent from 'components/ImageComponent/ImageComponent';
import { getSearchProviderTranslateKey } from 'helpers/addressHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import { AddressSearchProviderArray } from 'pages/settings/companySettings/models';
import React, { useRef, useState } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import Google from 'static/images/google.png';
import MapyCOM from 'static/images/mapycom.png';

import AutoCompleteTextInput from '../AutoCompleteTextInput';
import type { SelectItemWithDescription } from '../SelectInput';

interface AddressAutoCompleteInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  options?: RegisterOptions;
  items: SelectItemWithDescription[];
  defaultSearchProvider: AddressSearchProvider;
  enableSearchProviderSelect?: boolean;
  defaultSearchPhrase?: string;
  notFoundText?: React.ReactNode;
  isSearching?: boolean;
  handleSwitchToAllFields?: () => void;
  handleSelect: (id: string, provider: AddressSearchProvider) => Promise<void>;
  loadAddresses: (searchPhrase: string, provider: AddressSearchProvider) => Promise<void>;
  customOnBlur?: () => void;
}

const AddressAutoCompleteInput = ({
  name,
  label,
  placeholder,
  required,
  options,
  items,
  defaultSearchProvider,
  enableSearchProviderSelect,
  defaultSearchPhrase,
  notFoundText,
  isSearching,
  handleSelect,
  loadAddresses,
  handleSwitchToAllFields,
  customOnBlur
}: AddressAutoCompleteInputProps) => {
  const { tCommon, tInput } = useCustomTranslation();

  const searchRef = useRef<InputRef>(null);

  const [searchPhrase, setSearchPhrase] = useState(defaultSearchPhrase || '');
  const [searchProvider, setSearchProvider] = useState(defaultSearchProvider);
  const [openAddressItems, setOpenAddressItems] = useState<boolean>();

  return (
    <AutoCompleteTextInput
      name={name}
      required={required}
      options={options}
      items={items}
      label={label}
      placeholder={placeholder}
      ref={searchRef}
      onSelect={async (id) => await handleSelect(id, searchProvider)}
      open={openAddressItems}
      notFoundText={
        <div className="AddressAutoCompleteInput_notFound">
          {notFoundText || tInput('address_result_area')}
        </div>
      }
      customOnBlur={() => {
        setOpenAddressItems(undefined);
        customOnBlur?.();
      }}
      onSearch={async (phrase) => {
        setSearchPhrase(phrase);
        await loadAddresses(phrase, searchProvider);
      }}
      isSearching={isSearching}
      addon={
        enableSearchProviderSelect && (
          <div className="AddressAutoCompleteInput_searchProvider">
            <Icon path={mdiMagnify} />
            <Select
              value={searchProvider}
              onChange={async (provider) => {
                setSearchProvider(provider);
                await loadAddresses(searchPhrase, provider);
                setOpenAddressItems(true);
                searchRef.current?.focus();
              }}
              className="AddressAutoCompleteInput_searchProvider-select"
              options={AddressSearchProviderArray.map((provider) => ({
                value: provider,
                label: tCommon(getSearchProviderTranslateKey(provider))
              }))}
            />
          </div>
        )
      }
      autocompleteFooter={
        <div className="AddressAutoCompleteInput_footer">
          {handleSwitchToAllFields && (
            <div className="AddressAutoCompleteInput_switchToAllFields">
              <span onClick={() => handleSwitchToAllFields()}>
                {tInput('address_add_manually')}
              </span>
            </div>
          )}
          <div className="AddressAutoCompleteInput_search">
            <span>{tInput('address_search_provider_footer')}</span>
            {searchProvider === 'Google' ? (
              <ImageComponent url={Google} className="google-logo" />
            ) : (
              <ImageComponent url={MapyCOM} className="mapycom-logo" />
            )}
          </div>
        </div>
      }
    />
  );
};

export default AddressAutoCompleteInput;
